import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { IDeleteResponse, IPolicyForm } from '@/interfaces/common.model';
import { getFilterUrl, showToastNotification } from '@/utility/helper';
import { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Modal } from 'reactstrap';
import CardLayout from '../Layout/CardLayout';
import PrimaryInput from '../ui/PrimaryInput';
import { errorTypes } from '@/utility/validation';
import Button from '../ui/Button';
import FieldLayout from '../Layout/FieldLayout';
import Ckeditor from '../common/CkeEditor';
import SearchSelectBox, { ISelectOption } from '../ui/SearchSelectBox';
import ErrorMessage from '../common/ErrorMessage';
import { ICompanyResponse } from '@/interfaces/company.model';
import Label from '../ui/Label';
interface IAddPolicyModalProps {
  isOpen: boolean;
  EditData: IPolicyForm | null;
  onClose: () => void;
  getPolicy?: () => void;
}
const AddPolicyModal = ({
  isOpen,
  EditData,
  onClose,
  getPolicy,
}: IAddPolicyModalProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<IPolicyForm>();
  useEffect(() => {
    if (!isOpen) {
      reset({
        title: '',
        content: '',
      });
    }
  }, [isOpen, reset]);

  useEffect(() => {
    if (EditData) {
      setValue('title', EditData?.title);
      setValue('content', EditData?.content);
      setValue('companyId', EditData?.companyId);
    } else {
      reset();
    }
  }, [EditData]);
  const onSubmit: SubmitHandler<IPolicyForm> = async (data) => {
    setIsLoading(true);

    try {
      const API =
        EditData !== null
          ? API_URL +
          API_ENDPOINT.updatePolicy
          + EditData?.id
          : API_URL +
          API_ENDPOINT?.addPolicy
      const response: IDeleteResponse = await callApi(API, {
        method: 'POST',
        data: data,
      });
      setIsLoading(false);
      if (response?.status === true) {
        showToastNotification('success', response?.message);
        if (getPolicy) {
          getPolicy();
        }
        onClose();
        reset();
      } else {
        showToastNotification('danger', response?.message);
        reset();
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsLoading(false);
    }
  };
  const getCompany = async (inputValue?: string): Promise<ISelectOption[]> => {
    const filter = {
      search: inputValue,
    };

    let api = `${API_URL}${API_ENDPOINT.getCompany}`;

    if (inputValue) {
      api = `${API_URL}${API_ENDPOINT.getCompany}?${getFilterUrl(filter)}`;
    }

    try {
      const response: ICompanyResponse = await callApi(api, {
        method: 'GET',
      });
      // if (!watch('companyId')) {
      //   setValue('companyId', response?.data?.company[0]?.id);
      // }
      // setCompanyList(response?.data?.company);
      if (response.status && response.data?.company?.length) {
        return response.data.company.map((company) => ({
          value: company.id,
          label: company.name,
        }));
      }

      return [];
    } catch (error) {
      console.error('Error fetching company:', error);
      return [];
    }
  };
  return (
    <Modal
      className="modal-dialog modal-dialog-centered modal-md"
      isOpen={isOpen}
    >
      <div className="modal-content shadow-lg">
        <div className="modal-header p-4">
          <h1
            className="modal-title fs-3 rajdhani-semibold"
            id="addHolidayModalLabel"
          >
            {EditData ? 'Edit' : 'Add'}Document
          </h1>
          <button
            type="button"
            className="btn-close"
            onClick={() => {
              clearErrors();
              onClose();
              //   reset();
            }}
          />
        </div>
        <div className="modal-body p-4">
          <form onSubmit={handleSubmit(onSubmit)}>
            <CardLayout cardStyle="shadow-none mb-0">
              <div className="row">
                <FieldLayout fieldStyle="col-lg-12">
                  <Label required={true} id="companyId">
                    Select Company
                  </Label>
                  <Controller
                    name="companyId"
                    control={control}
                    rules={errorTypes.required('Company')}
                    render={({ field }) => (
                      <SearchSelectBox
                        value={field.value}
                        title="Select Company"
                        onChange={(option) => field.onChange(option?.value || '')}
                        isAsync
                        fetchOptions={getCompany}
                        defaultFetch
                        disable={isLoading}
                        isUsingFilter
                      />
                    )}
                  />
                  <ErrorMessage message={errors?.companyId?.message} />
                </FieldLayout>
                <div className="col-12">
                  <div className="form-group mb-3">
                    <PrimaryInput
                      label="Title"
                      type="text"
                      id="title"
                      placeholder="Enter document title"
                      required={true}
                      {...register('title', {
                        ...errorTypes.required(),
                        ...errorTypes.capitalName,
                      })}
                      errors={errors?.title?.message}
                    />
                  </div>
                </div>
                <FieldLayout fieldStyle="col-12 col-lg-12">
                  <Label id={'content'} required={true}>
                    Content
                  </Label>
                  <Controller
                    name="content"
                    control={control}
                    rules={{
                      required: 'Content is required',
                    }}
                    render={({ field }) => (
                      <Ckeditor
                        {...field}
                        value={field.value}
                        onChange={field.onChange}
                        error={errors?.content?.message}
                      />
                    )}
                  />
                </FieldLayout>
              </div>
            </CardLayout>
            <div className="modal-footer p-2">
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => {
                  clearErrors();
                  onClose();
                  reset();
                }}
              >
                Cancel
              </button>
              <Button
                type="submit"
                className="btn btn-primary"
                isLoading={isLoading}
              >
                {EditData ? 'Edit Document' : 'Add Document'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};
export default AddPolicyModal;
